// src/components/DocumentForgery/Batch/BatchUpload.jsx
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UploadCloud, FileText, MoreVertical, X, Folder,
  ChevronDown, ChevronUp, Settings, AlertCircle,
  File, Plus, Clock,
} from 'react-feather';
import { submitBatchHandler } from '../../../services/documentForgery';
import { showSuccessToast, showErrorToast } from '../../../utility/helper';

// ── Constants ─────────────────────────────────────────────────────────────────
const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPTED  = '.pdf,.png,.jpg,.jpeg,.webp,.tiff';
const SUPPORTED_TYPES = ['application/pdf','image/jpeg','image/png','image/webp','image/tiff'];

const LLM_OPTIONS = [
  { value: 'gpt-4.1',        label: 'GPT-4.1  (recommended)' },
  { value: 'gpt-4o',         label: 'GPT-4o' },
  { value: 'claude-3-5',     label: 'Claude 3.5 Sonnet' },
  { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
];
const OCR_OPTIONS = [
  { value: 'azure_ocr',       label: 'Azure OCR  (recommended)' },
  { value: 'google_document', label: 'Google Document AI' },
  { value: 'aws_textract',    label: 'AWS Textract' },
];

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024)        return `${b} B`;
  if (b < 1048576)     return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const validateFile = (f) => {
  const ext = f.name.split('.').pop()?.toLowerCase();
  const validExt = ['pdf','png','jpg','jpeg','webp','tiff'].includes(ext);
  if (!SUPPORTED_TYPES.includes(f.type) && !validExt) return 'Unsupported file type';
  if (f.size > MAX_BYTES) return 'Exceeds 20 MB limit';
  return null;
};

// ── Sub-components matching DocumentUpload patterns ───────────────────────────

// Spinner — mirrors the scan gif "processing" look  
const BatchSpinner = ({ fileCount, sourceTab }) => (
  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Animated ring */}
    <div style={{ position: 'relative', width: 72, height: 72, marginBottom: 12 }}>
      <svg width="72" height="72" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="36" cy="36" r="30" fill="none" stroke="#e5e7eb" strokeWidth="5" />
        <circle cx="36" cy="36" r="30" fill="none" stroke="#44ace4" strokeWidth="5"
          strokeDasharray="188" strokeDashoffset="47"
          strokeLinecap="round"
          style={{ animation: 'batchSpin 1.4s linear infinite' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <UploadCloud size={22} color="#44ace4" />
      </div>
    </div>
    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#111827' }}>Submitting batch…</p>
    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
      {sourceTab === 'files'
        ? `Uploading ${fileCount} document${fileCount !== 1 ? 's' : ''}`
        : 'Connecting to S3 directory'}
    </p>
    <style>{`
      @keyframes batchSpin {
        to { stroke-dashoffset: -188; }
      }
    `}</style>
  </div>
);

// How-it-works steps — shown in right panel when idle
const HowItWorks = ({ maxWorkers }) => (
  <div style={{ width: '100%' }}>
    <p style={{ margin: '0 0 18px', fontWeight: 800, fontSize: 13, color: '#111827', textAlign: 'center' }}>
      How Bulk Analysis Works
    </p>
    {[
      { n: '1', title: 'Submit',  desc: 'Upload files or provide an S3 path. We respond instantly with a tracking ID.' },
      { n: '2', title: 'Process', desc: `Documents run in parallel (${maxWorkers} at a time) through OCR → LLM → fraud checks.` },
      { n: '3', title: 'Results', desc: 'View per-document risk scores, issues, and download a full CSV report.' },
    ].map(s => (
      <div key={s.n} style={{ display: 'flex', gap: 12, marginBottom: 16, textAlign: 'left' }}>
        <div style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          background: '#44ace4', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 800,
        }}>{s.n}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{s.title}</div>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>{s.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

// File row — mirrors .document-card structure exactly
const FileItem = ({ file, error, menuOpen, onMenu, onDelete, index }) => (
  <div className="document-card">
    <div className="doc-left">
      {/* Thumbnail */}
      {file.type.startsWith('image/') ? (
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          style={{ width: 52, height: 64, objectFit: 'cover', borderRadius: 5, border: '1px solid #e5e7eb' }}
        />
      ) : (
        <div style={{
          width: 52, height: 64, background: error ? '#fff5f5' : '#f0f7ff',
          borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${error ? '#fecaca' : '#dbeafe'}`,
        }}>
          <FileText size={20} color={error ? '#dc2626' : '#44ace4'} />
        </div>
      )}
      <div className="doc-info">
        <span className="doc-name" title={file.name}>{file.name}</span>
        <span style={{ fontSize: 11, color: error ? '#dc2626' : '#9ca3af' }}>
          {error || formatBytes(file.size)}
        </span>
      </div>
    </div>

    <div className="doc-right">
      <div style={{ position: 'relative' }}>
        <MoreVertical
          size={16}
          onClick={() => onMenu(menuOpen === index ? null : index)}
          style={{ cursor: 'pointer', color: '#bbb' }}
        />
        {menuOpen === index && (
          <div style={{
            position: 'absolute', top: 24, right: 0,
            background: '#fff', border: '1px solid #ddd',
            borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10,
          }}>
            <div
              style={{ padding: '9px 14px', cursor: 'pointer', color: '#e74c3c', fontSize: 13, fontWeight: 500 }}
              onClick={() => { onDelete(file); onMenu(null); }}
            >
              Remove
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const BatchUpload = () => {
  const navigate   = useNavigate();
  const fileRef    = useRef(null);

  // Source
  const [sourceTab,  setSourceTab]  = useState('files');  // 'files' | 'directory'

  // Files
  const [files,      setFiles]      = useState([]);
  const [fileErrors, setFileErrors] = useState({});
  const [menuOpen,   setMenuOpen]   = useState(null);
  const [dragging,   setDragging]   = useState(false);

  // Directory
  const [dirUrl,     setDirUrl]     = useState('');
  const [accessKey,  setAccessKey]  = useState('');
  const [secretKey,  setSecretKey]  = useState('');
  const [region,     setRegion]     = useState('ap-south-1');

  // Config
  const [docType,     setDocType]     = useState('INVOICE');
  const [llmModel,    setLlmModel]    = useState('gpt-4.1');
  const [ocrModel,    setOcrModel]    = useState('azure_ocr');
  const [maxWorkers,  setMaxWorkers]  = useState(2);
  const [showConfig,  setShowConfig]  = useState(false);

  // Submit
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');
  const [submitted,    setSubmitted]    = useState(false);

  // ── File handling ───────────────────────────────────────────────────────────
  const addFiles = useCallback((incoming) => {
    const arr = Array.from(incoming);
    const errs = {};
    arr.forEach(f => { const e = validateFile(f); if (e) errs[f.name] = e; });
    setFileErrors(prev => ({ ...prev, ...errs }));
    setFiles(prev => {
      const names = new Set(prev.map(x => x.name));
      return [...prev, ...arr.filter(f => !names.has(f.name))];
    });
  }, []);

  const removeFile = (f) => {
    setFiles(prev => prev.filter(x => x !== f));
    setFileErrors(prev => { const n = { ...prev }; delete n[f.name]; return n; });
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError('');

    if (sourceTab === 'files') {
      if (!files.length)                                  return setSubmitError('Please add at least one file.');
      const valid = files.filter(f => !fileErrors[f.name]);
      if (!valid.length)                                  return setSubmitError('All files have errors — please remove them.');
    } else {
      if (!dirUrl.trim())                                 return setSubmitError('S3 directory URL is required.');
      if (!dirUrl.startsWith('s3://'))                   return setSubmitError('URL must start with s3://');
      if (!accessKey.trim())                              return setSubmitError('AWS Access Key is required.');
      if (!secretKey.trim())                              return setSubmitError('AWS Secret Key is required.');
    }

    setSubmitting(true);
    setSubmitted(false);
    try {
      let res;
      if (sourceTab === 'files') {
        const valid = files.filter(f => !fileErrors[f.name]);
        const fd    = new FormData();
        valid.forEach(f => fd.append('files[]', f));
        fd.append('doc_type',    docType);
        fd.append('llm_model',   llmModel);
        fd.append('ocr_model',   ocrModel);
        fd.append('max_workers', String(maxWorkers));
        res = await submitBatchHandler(fd, true);
      } else {
        res = await submitBatchHandler({
          directory_url: dirUrl.trim(),
          directory_credentials: {
            access_key: accessKey.trim(),
            secret_key: secretKey.trim(),
            region:     region.trim() || 'ap-south-1',
          },
          doc_type:    docType,
          llm_model:   llmModel,
          ocr_model:   ocrModel,
          max_workers: maxWorkers,
        }, false);
      }

      const { ref_id, total_documents } = res.data;
      showSuccessToast(`Batch submitted — ${total_documents} document(s) queued`);
      navigate(`/batch-result/${ref_id}`, { state: { ref_id, total_documents } });

    } catch (err) {
      setSubmitting(false);
      const detail = err?.response?.data;
      const msg = typeof detail === 'string'
        ? detail
        : Object.values(detail || {})?.[0]?.[0]
        || 'Submission failed. Please try again.';
      setSubmitError(msg);
      showErrorToast(msg);
    }
  };

  const validFiles   = files.filter(f => !fileErrors[f.name]);
  const invalidFiles = files.filter(f =>  fileErrors[f.name]);

  const canSubmit = !submitting && (
    sourceTab === 'files'
      ? validFiles.length > 0
      : dirUrl.trim() && accessKey.trim() && secretKey.trim()
  );

  return (
    <div className="document-area">

      {/* ══ LEFT PANEL ═══════════════════════════════════════════════════════ */}
      <div className="document-area__inner">

        {/* Header — mirrors DocumentUpload header exactly */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ color: 'black', marginBottom: 0, fontWeight: 600 }}>Bulk Analysis</h3>
            <p className="paragraph">Upload multiple documents or point to an S3 directory</p>
          </div>
          <button
            onClick={() => navigate('/batch-upload/history')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              background: '#f0f7ff', border: '1px solid #d8eef8',
              color: '#44ace4', fontWeight: 600, fontSize: 12,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <File size={13} />
            Batch History
          </button>
        </div>

        {/* Notification panel — same as DocumentUpload */}
        <div className="notification-panel">
          <h4>Analyse documents in bulk</h4>
          <span className="notification-panel__discription">
            Submit up to 50 files at once or connect an S3 folder. Results are processed in parallel and exported as a CSV.
          </span>
        </div>

        {/* ── Source toggle ── */}
        <div style={{
          display: 'flex', marginTop: 20, marginBottom: 16,
          borderRadius: 8, overflow: 'hidden',
          border: '1px solid #e5e7eb', background: '#f9fafb',
        }}>
          {[
            { key: 'files',     icon: <UploadCloud size={13} />, label: 'Upload Files'    },
            { key: 'directory', icon: <Folder      size={13} />, label: 'S3 Directory URL' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setSourceTab(tab.key); setSubmitError(''); }}
              style={{
                flex: 1, padding: '10px 12px', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontWeight: 600, fontSize: 12,
                background: sourceTab === tab.key ? '#44ace4' : 'transparent',
                color:      sourceTab === tab.key ? '#fff'    : '#6b7280',
                transition: 'all 0.15s',
              }}
            >
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* ── File upload source ── */}
        {sourceTab === 'files' && (<>
          {/* Dropzone — same pattern as DocumentUpload */}
          {files.length === 0 && (
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? '#44ace4' : '#ccd6e0'}`,
                borderRadius: 8, padding: '32px 24px',
                textAlign: 'center', color: '#555', cursor: 'pointer',
                background: dragging ? '#f0f7ff' : '#f8fbff',
                transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#44ace4'; e.currentTarget.style.background = '#f0f7ff'; }}
              onMouseLeave={e => { if (!dragging) { e.currentTarget.style.borderColor = '#ccd6e0'; e.currentTarget.style.background = '#f8fbff'; }}}
            >
              <UploadCloud size={44} color={dragging ? '#44ace4' : '#44ace4'} style={{ marginBottom: 12, opacity: 0.8 }} />
              <p style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                {dragging ? 'Drop files here' : 'Drag & drop files, or click to browse'}
              </p>
              <p style={{ fontSize: 12, color: '#999', margin: 0 }}>
                PDF, PNG, JPG, WEBP, TIFF — max 20 MB each, up to 50 files
              </p>
              <input
                ref={fileRef} type="file" multiple accept={ACCEPTED}
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
              />
            </div>
          )}

          {/* File list — uses .document-list + .document-card like DocumentUpload */}
          {files.length > 0 && (<>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                {validFiles.length} valid
                {invalidFiles.length > 0 && (
                  <span style={{ color: '#dc2626', marginLeft: 6 }}>· {invalidFiles.length} with errors</span>
                )}
              </span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => fileRef.current?.click()}
                  style={{ fontSize: 11, color: '#44ace4', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                >
                  + Add more
                </button>
                <button
                  onClick={() => { setFiles([]); setFileErrors({}); }}
                  style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                >
                  Clear all
                </button>
              </div>
              <input
                ref={fileRef} type="file" multiple accept={ACCEPTED}
                style={{ display: 'none' }}
                onChange={e => addFiles(e.target.files)}
              />
            </div>

            <div className="document-list" style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 2 }}>
              {files.map((file, index) => (
                <FileItem
                  key={file.name}
                  file={file}
                  index={index}
                  error={fileErrors[file.name]}
                  menuOpen={menuOpen}
                  onMenu={setMenuOpen}
                  onDelete={removeFile}
                />
              ))}
            </div>
          </>)}
        </>)}

        {/* ── Directory source ── */}
        {sourceTab === 'directory' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: '#fffbeb', border: '1px solid #fde68a33',
              fontSize: 12, color: '#92400e', lineHeight: 1.6,
              display: 'flex', gap: 8,
            }}>
              <AlertCircle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>All supported files (PDF, JPG, PNG, WEBP, TIFF) in the S3 folder will be discovered and analysed automatically. Credentials are used only for this request.</span>
            </div>

            {/* S3 URL */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
                S3 Directory URL <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                value={dirUrl} onChange={e => setDirUrl(e.target.value)}
                placeholder="s3://my-bucket/invoices/april-2024/"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8,
                  border: '1px solid #d1d5db', fontSize: 13, outline: 'none',
                  boxSizing: 'border-box', color: '#111827',
                }}
              />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Must start with s3:// and end with /</div>
            </div>

            {/* Access key + Secret key */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
                  Access Key ID <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  value={accessKey} onChange={e => setAccessKey(e.target.value)}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
                  Secret Access Key <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="password"
                  value={secretKey} onChange={e => setSecretKey(e.target.value)}
                  placeholder="••••••••••••••••••••"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827' }}
                />
              </div>
            </div>

            {/* Region */}
            <div style={{ maxWidth: 220 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>AWS Region</label>
              <input
                value={region} onChange={e => setRegion(e.target.value)}
                placeholder="ap-south-1"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827' }}
              />
            </div>
          </div>
        )}

        {/* ── Analysis settings (collapsible) — matches CustomFields button style ── */}
        <div style={{ marginTop: 20, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <button
            onClick={() => setShowConfig(v => !v)}
            style={{
              width: '100%', padding: '11px 14px', border: 'none',
              background: '#f9fafb', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: '#374151' }}>
              <Settings size={13} color="#6b7280" />
              Analysis Settings
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                {docType} · {llmModel} · {maxWorkers} worker{maxWorkers > 1 ? 's' : ''}
              </span>
            </span>
            {showConfig ? <ChevronUp size={14} color="#9ca3af" /> : <ChevronDown size={14} color="#9ca3af" />}
          </button>

          {showConfig && (
            <div style={{ padding: '16px 14px', borderTop: '1px solid #e5e7eb', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* Doc type */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Document Type</label>
                <select value={docType} onChange={e => setDocType(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 13, color: '#111827', background: '#fff', cursor: 'pointer' }}>
                  <option value="INVOICE">Invoice</option>
                  <option value="BILL">Bill</option>
                </select>
              </div>

              {/* Workers slider */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>
                  Parallel Workers <span style={{ background: '#e0f2fe', color: '#0284c7', borderRadius: 4, padding: '1px 6px', fontWeight: 800 }}>{maxWorkers}</span>
                </label>
                <input type="range" min="1" max="10" value={maxWorkers}
                  onChange={e => setMaxWorkers(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#44ace4' }}
                />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Higher = faster, more API cost (max 10)</div>
              </div>

              {/* LLM */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>LLM Model</label>
                <select value={llmModel} onChange={e => setLlmModel(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 13, color: '#111827', background: '#fff', cursor: 'pointer' }}>
                  {LLM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* OCR */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>OCR Model</label>
                <select value={ocrModel} onChange={e => setOcrModel(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 13, color: '#111827', background: '#fff', cursor: 'pointer' }}>
                  {OCR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* ── Error ── */}
        {submitError && (
          <div style={{
            marginTop: 14, display: 'flex', gap: 8,
            padding: '10px 12px', borderRadius: 8,
            background: '#fff5f5', border: '1px solid #fecaca',
          }}>
            <AlertCircle size={14} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: '#dc2626' }}>{submitError}</span>
          </div>
        )}

        {/* ── Submit button ── */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: '100%', marginTop: 16,
            padding: '13px',
            background: canSubmit ? '#44ace4' : '#93c5fd',
            color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 14,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'background 0.2s',
          }}
        >
          <UploadCloud size={16} />
          {submitting
            ? 'Submitting…'
            : sourceTab === 'files'
              ? `Analyse ${validFiles.length || ''} Document${validFiles.length !== 1 ? 's' : ''}`
              : 'Analyse S3 Directory'}
        </button>

      </div>
      {/* ══ END LEFT PANEL ═══════════════════════════════════════════════════ */}


      {/* ══ RIGHT PREVIEW PANEL — mirrors .document-preview ══════════════════ */}
      <div className="document-preview">
        <h2>InsightAI Bulk Analyst</h2>

        <div className="document-preview__analysis">
          {submitting ? (
            <BatchSpinner
              fileCount={validFiles.length}
              sourceTab={sourceTab}
            />
          ) : files.length > 0 || (sourceTab === 'directory' && dirUrl) ? (
            /* Ready state — show summary */
            <div style={{ width: '100%', textAlign: 'center' }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%',
                background: '#e0f2fe', margin: '0 auto 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UploadCloud size={26} color="#0284c7" />
              </div>
              <p style={{ fontWeight: 700, fontSize: 14, color: '#111827', margin: '0 0 4px' }}>
                {sourceTab === 'files'
                  ? `${validFiles.length} document${validFiles.length !== 1 ? 's' : ''} ready`
                  : 'S3 directory configured'}
              </p>
              <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 20px' }}>
                {sourceTab === 'files'
                  ? `${maxWorkers} parallel worker${maxWorkers > 1 ? 's' : ''} · ${llmModel} · ${ocrModel}`
                  : `${dirUrl.slice(0, 40)}${dirUrl.length > 40 ? '…' : ''}`}
              </p>

              {/* Mini stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
                {[
                  { label: 'Model',   val: llmModel  },
                  { label: 'OCR',     val: ocrModel  },
                  { label: 'Workers', val: maxWorkers },
                  { label: 'Type',    val: docType    },
                ].map(s => (
                  <div key={s.label} style={{
                    padding: '8px 10px', borderRadius: 7,
                    background: '#f8fafc', border: '1px solid #e5e7eb', textAlign: 'left',
                  }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{s.val}</div>
                  </div>
                ))}
              </div>

              {invalidFiles.length > 0 && (
                <div style={{ fontSize: 11, color: '#d97706', marginTop: 8, padding: '6px 10px', background: '#fffbeb', borderRadius: 6 }}>
                  ⚠ {invalidFiles.length} file{invalidFiles.length > 1 ? 's' : ''} will be skipped (errors)
                </div>
              )}
            </div>
          ) : (
            /* Idle state — matches DocumentUpload idle look */
            <>
              <UploadCloud size={28} color="#d1d5db" />
              <p style={{ color: 'black', fontWeight: 800, marginBottom: 0, marginTop: 18, fontSize: 14 }}>
                Add documents to get started
              </p>
              <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 24 }}>
                Bulk analysis will appear here
              </p>
              <HowItWorks maxWorkers={maxWorkers} />
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default BatchUpload;